/**
 * .synth → Flow importer.
 *
 * Uses the existing backend .synth parser (POST /api/upload → GET
 * /api/beatmaps/{id}) instead of re-implementing the format client-side, then
 * maps the parsed VR beatmap onto Flow's lane model:
 *
 *  - Each difficulty's notes → Flow notes. Landscape (K-POP Flow) charts sit on
 *    a fixed lane grid and are mapped slot-for-slot, with the deck sized to the
 *    slots the chart actually reaches (3 / 4 / 5 lanes). A free-form VR grid
 *    map instead has its observed X range spread across 4 lanes.
 *  - VR rails (has_rail + rail_nodes, node = [x, y, timeMs * 0.02]) become
 *    Flow trails: head + decoded node beats as waypoints.
 *  - Special one-hand / both-hands notes flatten to taps.
 *  - BPM + offset come from the .synth itself (the analyzer's re-detection is
 *    overridden), audio is fetched from the backend and loaded for playback,
 *    and the song title/artist + timeline bookmarks come across too.
 *
 * The mapping is lossy by nature (a 3D grid folded onto lanes) — the goal is a
 * solid starting skeleton with correct timing, not a 1:1 conversion.
 */

import { useFlowStore } from './flowStore';
import { loadAudioFile } from './audioLoader';
import { API_BASE } from '../../api/apiBase';
import type {
  FlowChart, FlowNote, FlowLaneIndex, FlowTrailWaypoint, FlowLaneCount,
  FlowDifficultyName, FlowBookmark,
} from './types';
import { FLOW_DIFFICULTIES } from './types';

/** Lane count the fallback quantizer spreads a free-form VR grid onto. */
const IMPORT_LANES: FlowLaneCount = 4;

/**
 * Landscape (K-POP Flow) charts don't use the free VR grid — they place every
 * note on a fixed set of lane slots, `x = k * LANE_STEP`. Measured off charts
 * exported by the desktop editor: outer lanes at ±0.817…±0.821, inner at
 * ±0.4075…±0.4115, centre at ~0. Authoring jitter is a few thousandths, hence
 * the tolerance (expressed in step units).
 */
const LANDSCAPE_LANE_STEP = 0.409;
const GRID_TOLERANCE = 0.15;

interface VRNote {
  time_ms: number;
  time_beats: number;
  position: [number, number, number];
  note_type?: string;
  hand?: string;
  has_rail?: boolean;
  rail_nodes?: [number, number, number][] | null;
}
interface VRDifficulty { name?: string; notes: VRNote[]; }
interface VRBeatmap {
  id: string;
  /** The backend serialises the .synth header as name/author; title/artist are
   *  accepted too so an older payload still populates the song fields. */
  name?: string;
  author?: string;
  title?: string;
  artist?: string;
  bpm: number;
  /** Milliseconds (backend convention). */
  offset?: number;
  difficulties: Record<string, VRDifficulty>;
  bookmarks?: { beat: number; name?: string }[];
}

let _idSeed = 1;
const freshId = () => `syn-${Date.now().toString(36)}-${_idSeed++}`;

interface LaneMapping { laneOf: (x: number) => FlowLaneIndex; lanes: FlowLaneCount }

const clampLane = (i: number, lanes: FlowLaneCount) =>
  Math.max(0, Math.min(lanes - 1, i)) as FlowLaneIndex;

/** Share of note heads that must land on a grid for it to count as that grid.
 *  Not 100%: a single hand-nudged note shouldn't drop a whole chart onto the
 *  free-form fallback (a real export had exactly one such stray). */
const GRID_MATCH_RATIO = 0.95;

const fitsWholeStep = (u: number) =>
  Math.abs(u - Math.round(u)) < GRID_TOLERANCE && Math.abs(u) <= 2 + GRID_TOLERANCE;
const fitsHalfStep = (u: number) =>
  Math.abs(u * 2 - Math.round(u * 2)) < GRID_TOLERANCE * 2 && Math.abs(u) <= 1.5 + GRID_TOLERANCE;

/**
 * Work out how this chart's X positions become Flow lanes.
 *
 * Landscape charts sit on the fixed lane grid, so they're mapped slot-for-slot
 * and the deck is sized to the slots the chart actually reaches (a chart that
 * never uses the centre lane keeps its gap instead of being squashed). Anything
 * else — a real VR grid map with continuous X — falls back to spreading the
 * observed range over 4 lanes.
 *
 * The grid is decided from the note HEADS alone: rail nodes may be dragged
 * anywhere between lanes, so they get snapped onto whatever grid the heads
 * establish rather than being allowed to veto it.
 */
function makeLaneMapping(headX: number[], allX: number[]): LaneMapping {
  if (headX.length === 0) {
    return { laneOf: () => 0 as FlowLaneIndex, lanes: IMPORT_LANES };
  }
  const units = headX.map(x => x / LANDSCAPE_LANE_STEP);
  const share = (fits: (u: number) => boolean) =>
    units.reduce((n, u) => n + (fits(u) ? 1 : 0), 0) / units.length;

  // Odd deck (3 or 5 lanes): slots on whole steps, centre lane at x = 0.
  if (share(fitsWholeStep) >= GRID_MATCH_RATIO) {
    const reach = Math.max(
      ...units.filter(fitsWholeStep).map(u => Math.abs(Math.round(u))),
    );
    const lanes: FlowLaneCount = reach >= 2 ? 5 : 3;
    const centre = (lanes - 1) / 2;
    return { laneOf: x => clampLane(Math.round(x / LANDSCAPE_LANE_STEP) + centre, lanes), lanes };
  }

  // Even deck (4 lanes): slots on half steps (±0.5, ±1.5) — no centre lane.
  if (share(fitsHalfStep) >= GRID_MATCH_RATIO) {
    return { laneOf: x => clampLane(Math.round(x / LANDSCAPE_LANE_STEP - 0.5) + 2, 4), lanes: 4 };
  }

  // Free-form VR grid: spread the observed range across the lanes. round() (not
  // floor()) so the extremes land on the outer lanes and the middle positions
  // don't pile onto one lane.
  let min = Infinity, max = -Infinity;
  for (const x of allX) { if (x < min) min = x; if (x > max) max = x; }
  const span = max - min;
  if (span < 1e-6) {
    const mid = Math.floor(IMPORT_LANES / 2) as FlowLaneIndex;
    return { laneOf: () => mid, lanes: IMPORT_LANES };
  }
  return {
    laneOf: x => clampLane(Math.round(((x - min) / span) * (IMPORT_LANES - 1)), IMPORT_LANES),
    lanes: IMPORT_LANES,
  };
}

/** Decode a VR rail node's encoded time (timeMs * 0.02) into chart beats. */
function nodeBeat(enc: number, bpm: number): number {
  return (enc / 0.02 / 1000) * (bpm / 60);
}

function mapDifficulty(
  vr: VRDifficulty, bpm: number, offsetSeconds: number,
): { notes: FlowNote[]; lanes: FlowLaneCount } {
  const { laneOf, lanes } = makeLaneMapping(
    vr.notes.map(n => n.position[0]),
    vr.notes.flatMap(n => [n.position[0], ...(n.rail_nodes ?? []).map(w => w[0])]),
  );
  const out: FlowNote[] = [];
  for (const n of vr.notes) {
    const lane = laneOf(n.position[0]);
    const beat = n.time_beats;
    const base: FlowNote = {
      id: freshId(),
      lane,
      time_beats: beat,
      time_seconds: (beat / Math.max(1, bpm)) * 60 + offsetSeconds,
      type: 'tap',
      duration_beats: 0,
    };
    if (n.has_rail && n.rail_nodes && n.rail_nodes.length > 0) {
      const waypoints: FlowTrailWaypoint[] = [
        { lane, time_beats: beat },
        ...n.rail_nodes.map(w => ({ lane: laneOf(w[0]), time_beats: nodeBeat(w[2], bpm) })),
      ].sort((a, b) => a.time_beats - b.time_beats);
      if (waypoints[waypoints.length - 1].time_beats > beat + 1e-4) {
        base.waypoints = waypoints;
        base.duration_beats = waypoints[waypoints.length - 1].time_beats - waypoints[0].time_beats;
      }
    }
    out.push(base);
  }
  out.sort((a, b) => a.time_beats - b.time_beats);
  return { notes: out, lanes };
}

/**
 * Import a .synth file into the Flow editor. Returns the imported song name.
 * Throws with a friendly message on any failure.
 */
export async function importSynthToFlow(file: File): Promise<string> {
  // 1) Let the backend parse the .synth (existing, battle-tested parser).
  const form = new FormData();
  form.append('files', file, file.name);
  const upRes = await fetch(`${API_BASE}/api/upload?editor=flow-import`, { method: 'POST', body: form });
  if (!upRes.ok) throw new Error(`The .synth could not be parsed (${upRes.status})`);
  const batch = await upRes.json();
  const first = Array.isArray(batch?.results) ? batch.results[0] : batch?.results ?? batch;
  const mapId: string | undefined = first?.beatmap_id ?? first?.id ?? first?.beatmap?.id;
  if (!mapId) {
    // Surface the SERVER's own words — the generic "no beatmap id came back"
    // hid the real reason (a prod-only filesystem error, an encrypted file, a
    // parse failure…) and made remote reports undiagnosable.
    const detail = first?.error || first?.message || batch?.message;
    throw new Error(detail ? `Import failed: ${detail}` : 'The .synth was parsed but no beatmap id came back');
  }

  // 2) Fetch the parsed beatmap JSON.
  const bmRes = await fetch(`${API_BASE}/api/beatmaps/${mapId}`);
  if (!bmRes.ok) throw new Error(`Could not fetch the parsed beatmap (${bmRes.status})`);
  const bm: VRBeatmap = await bmRes.json();
  const bpm = bm.bpm || 120;
  const offsetSeconds = (bm.offset ?? 0) / 1000;
  const songTitle = bm.name || bm.title || '';
  const songArtist = bm.author || bm.artist || '';

  // 3) Audio → File → the normal Flow audio loader (playback + waveform).
  const audioRes = await fetch(`${API_BASE}/api/beatmaps/${mapId}/audio`);
  if (audioRes.ok) {
    const blob = await audioRes.blob();
    const audioFile = new File([blob], `${songTitle || 'song'}.ogg`, { type: blob.type || 'audio/ogg' });
    try { await loadAudioFile(audioFile); } catch { /* analysis failure is non-fatal */ }
  }

  // 4) Map every difficulty present; the first non-empty becomes active.
  const store = useFlowStore.getState();
  const perDiff: Partial<Record<FlowDifficultyName, FlowNote[]>> = {};
  const laneCounts: Partial<Record<FlowDifficultyName, FlowLaneCount>> = {};
  for (const name of FLOW_DIFFICULTIES) {
    const vr = bm.difficulties?.[name];
    if (!vr || !Array.isArray(vr.notes) || vr.notes.length === 0) continue;
    const mapped = mapDifficulty(vr, bpm, offsetSeconds);
    perDiff[name] = mapped.notes;
    laneCounts[name] = mapped.lanes;
  }
  const names = Object.keys(perDiff) as FlowDifficultyName[];
  if (names.length === 0) throw new Error('The .synth has no notes in any difficulty');
  const active = names.includes('Hard') ? 'Hard' : names[0];

  const difficultyNotes: FlowChart['difficultyNotes'] = {};
  for (const n of names) { if (n !== active) difficultyNotes[n] = perDiff[n]; }

  // Timeline bookmarks ride along — the desktop editor's section markers are
  // the map's structure, and re-deriving them by ear is busywork.
  const bookmarks: FlowBookmark[] = (bm.bookmarks ?? [])
    .filter(b => Number.isFinite(b?.beat))
    .map(b => ({ time_beats: b.beat, label: b.name || undefined }))
    .sort((a, b) => a.time_beats - b.time_beats);

  // 5) Install the chart — .synth BPM/offset override the analyzer's guesses.
  store.setChart({
    bpm,
    offset_seconds: offsetSeconds,
    notes: perDiff[active] ?? [],
    difficultyNotes,
    laneCounts,
    bookmarks,
    meta: { title: songTitle || undefined, artist: songArtist || undefined },
  });
  store.setActiveDifficultyAfterLoad(active);
  store.setSongName(songTitle || file.name.replace(/\.synth$/i, ''));
  store.setSongAuthor(songArtist);
  store.setCurrentTime(0);
  store.clearSelection();
  return songTitle || file.name;
}
